import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Background,
  BackgroundVariant,
  Connection,
  Controls,
  Edge,
  Node,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { toPng } from "html-to-image";

import { AtlasNode } from "../components/AtlasNode";
import { ProcessNode } from "../components/ProcessNode";
import { CircleNode } from "../components/CircleNode";
import { EllipseNode } from "../components/EllipseNode";
import { PillNode } from "../components/PillNode";
import { HexagonNode } from "../components/HexagonNode";
import { ParallelogramNode } from "../components/ParallelogramNode";
import { TrapezoidNode } from "../components/TrapezoidNode";
import { TriangleNode } from "../components/TriangleNode";
import { CloudNode } from "../components/CloudNode";
import { StarNode } from "../components/StarNode";
import { CylinderNode } from "../components/CylinderNode";
import { DocumentNode } from "../components/DocumentNode";
import { OrthogonalEdge } from "../components/OrthogonalEdge";
import { GraphData, NodeData, EdgeData, api, blankNode, getAuthToken } from "../lib/api";
import { useNodeTypes } from "../lib/useNodeTypes";
import { useStructureTypes } from "../lib/useStructureTypes";
import { useStructureTypeConfig } from "../lib/useStructureTypeConfig";
import { getContactCounts, normalizeContactType } from "../lib/contacts";
import { NodeOverview } from "../components/NodeOverview";
import { ContactBookModal } from "../components/ContactBookModal";
import { ConfirmModal } from "../components/ConfirmModal";
import { InlineAlert } from "../components/InlineAlert";
import { HotkeysOverlay } from "../components/editor/HotkeysOverlay";
import { Sidebar } from "../components/editor/Sidebar";
import { Inspector } from "../components/editor/Inspector";
import { TopBar } from "../components/editor/TopBar";
import { Canvas } from "../components/editor/Canvas";
import { SettingsModal } from "../components/editor/SettingsModal";
import { PngExportControls } from "../components/editor/PngExportControls";
import { useEditorHotkeys } from "../hooks/useEditorHotkeys";
import { useTheme } from "../lib/theme";
import { useGraphHistory } from "../hooks/useGraphHistory";
import { useContacts } from "../hooks/useContacts";
import { useGraphMapping } from "../hooks/useGraphMapping";
import { useAutosave } from "../hooks/useAutosave";
import { useCollaboration } from "../hooks/useCollaboration";
import { useOverviewNavigation } from "../hooks/useOverviewNavigation";
import { useGraphData } from "../hooks/useGraphData";
import { useInspectorState } from "../hooks/useInspectorState";

type EditorInnerProps = {
  structureId?: string;
  shareToken?: string;
  viewOnly?: boolean;
};

type ConfirmDialog = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "default";
  onConfirm: () => void;
};

const nodeTypes = {
  atlas: AtlasNode,
  rectangle: AtlasNode,
  diamond: ProcessNode,
  circle: CircleNode,
  ellipse: EllipseNode,
  pill: PillNode,
  hexagon: HexagonNode,
  parallelogram: ParallelogramNode,
  trapezoid: TrapezoidNode,
  triangle: TriangleNode,
  cloud: CloudNode,
  star: StarNode,
  cylinder: CylinderNode,
  document: DocumentNode,
};
const edgeTypes = { orthogonal: OrthogonalEdge };

// Helper function to get the React Flow node type from shape
function getReactFlowType(shape?: string): string {
  if (!shape) return "atlas";
  // Map shape to node type, defaulting to atlas for unknown shapes
  return nodeTypes[shape as keyof typeof nodeTypes] ? shape : "atlas";
}

const AUTOSAVE_DELAY_MS = 1200;

const decodeJwtPayload = (token: string) => {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    const normalized = base64.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    return JSON.parse(atob(padded));
  } catch (err) {
    console.warn("[token] Failed to decode payload", err);
    return null;
  }
};

const getTokenPayloadForStructure = (structureId: string) => {
  const token = getAuthToken();
  if (!token) return null;
  try {
    const payload = decodeJwtPayload(token);
    if (!payload) return null;
    const expOk = !payload.exp || payload.exp * 1000 > Date.now();
    if (!expOk) return null;
    if (payload.sub !== structureId) return null;
    return payload;
  } catch (err) {
    console.warn("[token] Failed to parse token payload", err);
    return null;
  }
};

const normalizeHandleId = (handle?: string | null) => {
  if (!handle) return undefined;
  const h = String(handle).toLowerCase();
  const map: Record<string, string> = {
    "top-in": "top",
    "top-out": "top",
    "bottom-in": "bottom",
    "bottom-out": "bottom",
    "left-in": "left",
    "left-out": "left",
    "right-in": "right",
    "right-out": "right",
  };
  if (map[h]) return map[h];
  if (["top", "bottom", "left", "right"].includes(h)) return h;
  return undefined;
};

const EditorInner: React.FC<EditorInnerProps> = ({ structureId, shareToken, viewOnly = false }) => {
  const { structureTypes } = useStructureTypes();
  const [rfNodes, setRfNodes, _onNodesChange] = useNodesState<NodeData>([]);
  const [rfEdges, setRfEdges, _onEdgesChange] = useEdgesState([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [selectedEdgeIds, setSelectedEdgeIds] = useState<string[]>([]);
  const [hideInactive, setHideInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [dirtyTick, setDirtyTick] = useState(0);
  const [statusDismissed, setStatusDismissed] = useState(false);
  const [importing, setImporting] = useState(false);
  const [viewOnlyMode, setViewOnlyMode] = useState(viewOnly);
  const [newNodeType, setNewNodeType] = useState<string>("gruppe");
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState<"general" | "types" | "contacts" | "access" | "import" | "danger" | "help">("general");
  const [settingsName, setSettingsName] = useState("");
  const [settingsDescription, setSettingsDescription] = useState("");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null);
  const [settingsDeleteLoading, setSettingsDeleteLoading] = useState(false);
  const [showHotkeys, setShowHotkeys] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pngThemeChoice, setPngThemeChoice] = useState<"current" | "light" | "dark">("current");
  const [pngBackgroundChoice, setPngBackgroundChoice] = useState<"canvas" | "transparent">("canvas");
  const [pngExporting, setPngExporting] = useState(false);
  const [showPngControls, setShowPngControls] = useState(false);
  const [overviewNode, setOverviewNode] = useState<NodeData | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog | null>(null);
  const [contactBookOpen, setContactBookOpen] = useState(false);
  const applyEdgePatch = useCallback((edge: any, patch: Partial<EdgeData>) => {
    const nextData: EdgeData = { ...(edge.data as EdgeData), ...patch };
    const lineStyle = (nextData as any).line_style || "bezier";
    if (lineStyle === "step" && !nextData.routing) {
      nextData.routing = { mode: "auto", bendOffset: 0 };
    }
    const nextType =
      lineStyle === "step"
        ? "orthogonal"
        : lineStyle === "straight"
        ? "straight"
        : lineStyle === "smoothstep"
        ? "smoothstep"
        : "default";
    return { ...edge, type: nextType, data: nextData };
  }, []);
  const { theme, toggle, setTheme } = useTheme();
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const { contactLookup, contactLookupRef, resolveContacts, loadContactsForStructure } = useContacts();
  const { push, queue, cancelQueue, reset, undo, redo, canUndo, canRedo, markRemote, lockIndex } = useGraphHistory();
  const location = useLocation();
  const autoOpenSettingsFromNav = (location.state as any)?.autoOpenSettings || false;
  const applyGraphStateRef = useRef<(nodes: any[], edges: any[], opts?: { throttle?: boolean }) => void>();
  const toggleCollapseRef = useRef<(id: string) => void>();
  const initialSettingsOpenedRef = useRef(false);
  const applyGraphStateProxy = useCallback(
    (nodes: any[], edges: any[], opts?: { throttle?: boolean }) => applyGraphStateRef.current?.(nodes, edges, opts),
    []
  );
  const toggleCollapseProxy = useCallback((id: string) => toggleCollapseRef.current?.(id), []);
  const { structure, setStructure, loading, error, applySnapshot, loadGraph, setError } = useGraphData({
    structureId,
    shareToken,
    viewOnly,
    resolveContacts,
    toggleCollapse: toggleCollapseProxy,
    applyGraphState: applyGraphStateProxy,
    resetHistory: reset,
    setViewOnlyMode,
    loadContactsForStructure,
    initialSettingsOpenedRef,
    setShowSettings,
  });
  const structureTypeConfig = useStructureTypeConfig(structure, structureTypes);
  const { nodeTypes: nodeTemplates, nodeTypeById, refetch: refetchNodeTypes } = useNodeTypes(structureId, structure?.structure_type_key);
  const {
    draftNode,
    setDraftNode,
    draftDirty,
    setDraftDirty,
    inspectorTab,
    setInspectorTab,
    bulkNodeType,
    setBulkNodeType,
    bulkNodeColor,
    setBulkNodeColor,
    bulkTextColor,
    setBulkTextColor,
    bulkEdgeColor,
    setBulkEdgeColor,
    primarySelectedNodeId,
    primarySelectedEdgeId,
    selectedNode,
    selectedEdge,
    currentNodeData,
    selectedNodes,
    selectedEdges,
    hasSelection,
    isBulk,
  } = useInspectorState({ nodeTemplates, nodeTypeById, selectedNodeIds, selectedEdgeIds, rfNodes, rfEdges, resolveContacts, viewOnlyMode });
  const hotkeySections = useMemo(
    () => [
      {
        title: "Navigation",
        items: [
          { keys: "F", desc: "Fokus auf Auswahl" },
          { keys: "Shift+F", desc: "Fit View" },
          { keys: "Mod+F", desc: "Suche fokussieren" },
          { keys: "Shift (halten)", desc: "Rahmen ziehen für Mehrfachauswahl" },
        ],
      },
      {
        title: "Bearbeitung",
        items: [
          { keys: "Mod+Z / Mod+Shift+Z / Mod+Y", desc: "Undo / Redo" },
          { keys: "Delete / Backspace", desc: "Auswahl löschen" },
          { keys: "Mod+D", desc: "Node duplizieren" },
          { keys: "Mod+S", desc: "Speichern" },
          { keys: "N", desc: "Neuen Node hinzufügen" },
        ],
      },
      {
        title: "Edges",
        items: [
          { keys: "1 / 2 / 3", desc: "Linie: straight / step / bezier" },
          { keys: "R", desc: "Pfeilrichtung umkehren (reverse)" },
          { keys: "B", desc: "Pfeile an beiden Enden" },
          { keys: "N", desc: "Keine Pfeile" },
        ],
      },
      {
        title: "Sonstiges",
        items: [
          { keys: "?", desc: "Shortcut-Overlay ein/aus" },
          { keys: "Escape", desc: "Auswahl/Dialogs schließen" },
          { keys: "Ctrl/Cmd/Shift + Klick", desc: "Mehrfachauswahl" },
        ],
      },
    ],
    []
  );
  const renderCount = useRef(0);
  renderCount.current += 1;
  const sidebarOpenRef = useRef(sidebarOpen);
  const wasMobileRef = useRef<boolean | null>(null);
  const pendingFrameRef = useRef<number | null>(null);
  const flowWrapperRef = useRef<HTMLDivElement | null>(null);
  const pendingStateRef = useRef<{ nodes: any[]; edges: any[] } | null>(null);
  const saveQueuedRef = useRef(false);
  const savingRef = useRef(false);
  const { fitView } = useReactFlow();
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const { handleNavigateFromContactBook, openOverviewForNode } = useOverviewNavigation({
    rfNodes,
    fitView,
    setOverviewNode,
    setSelectedNodeIds,
    setSelectedEdgeIds,
    setContactBookOpen,
  });

  // Store current values in a ref to avoid dependency loops
  const rfStateRef = useRef<{ nodes: any[]; edges: any[] }>({ nodes: [], edges: [] });
  useEffect(() => {
    rfStateRef.current = { nodes: rfNodes, edges: rfEdges };
  }, [rfNodes, rfEdges]);

  const markDirty = useCallback(() => {
    setDirty(true);
    setDirtyTick((tick) => tick + 1);
  }, []);

  const contactsForSearch = useCallback(
    (node: NodeData) => {
      const list = [...((node.contacts as any[]) || [])];
      const ids = ((node as any).contact_ids || []) as string[];
      ids.forEach((id) => {
        const c = contactLookupRef.current[id];
        if (c) list.push(c as any);
      });
      return list;
    },
    []
  );

  const { mapGraphState } = useGraphMapping(hideInactive);

  const flushPendingFrame = useCallback(() => {
    const pending = pendingStateRef.current;
    if (!pending) return;
    setRfNodes(pending.nodes);
    setRfEdges(pending.edges);
    rfStateRef.current = { nodes: pending.nodes, edges: pending.edges };
    pendingStateRef.current = null;
    pendingFrameRef.current = null;
  }, [setRfEdges, setRfNodes]);

  const applyGraphState = useCallback(
    (nodes: any[], edges: any[], opts?: { throttle?: boolean }) => {
      const mapped = mapGraphState(nodes, edges);
      if (opts?.throttle) {
        pendingStateRef.current = mapped;
        if (!pendingFrameRef.current) {
          pendingFrameRef.current = requestAnimationFrame(flushPendingFrame);
        }
        return;
      }
      if (pendingFrameRef.current) {
        cancelAnimationFrame(pendingFrameRef.current);
        pendingFrameRef.current = null;
        pendingStateRef.current = null;
      }
      setRfNodes(mapped.nodes);
      setRfEdges(mapped.edges);
      rfStateRef.current = { nodes: mapped.nodes, edges: mapped.edges };
    },
    [flushPendingFrame, mapGraphState, setRfEdges, setRfNodes]
  );

  useEffect(() => {
    applyGraphStateRef.current = applyGraphState;
  }, [applyGraphState]);

  useEffect(() => {
    return () => {
      if (pendingFrameRef.current) {
        cancelAnimationFrame(pendingFrameRef.current);
      }
    };
  }, []);

  const buildGraphPayload = useCallback(
    (nodes: any[], edges: any[]): GraphData => {
      return {
        structure_id: structure?.id,
        nodes: nodes.map((n) => {
          const { highlighted, ...rest } = n.data as NodeData;
          const pos = n.position || (rest as any).position || { x: 0, y: 0 };
          const typeCfg = nodeTypeById(rest.type);
          const color = rest.color || typeCfg?.defaultColor || "#2563eb";
          const textColor = rest.text_color || typeCfg?.defaultTextColor;
          const normalizedContacts = (rest.contacts || []).map((c) => {
            const normalizedType = normalizeContactType(c.contact_type || (c as any).contactType || (c as any).type);
            return {
              ...c,
              contact_type: normalizedType || (c as any).contact_type || (c as any).type,
            };
          });
          return {
            ...rest,
            contacts: normalizedContacts,
            contact_ids: (rest as any).contact_ids || [],
            contact_roles: (rest as any).contact_roles || {},
            color,
            text_color: textColor,
            position: pos,
            x: pos.x,
            y: pos.y,
          };
        }),
        edges: edges.map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          source_handle: normalizeHandleId((e as Edge<EdgeData>).sourceHandle) || null,
          target_handle: normalizeHandleId((e as Edge<EdgeData>).targetHandle) || null,
          arrow_style: (e.data as any)?.arrow_style || "default",
          line_style: (e.data as any)?.line_style || "bezier",
          routing: (e.data as any)?.routing,
          label: (e.data as any)?.label,
          color: (e.data as any)?.color || "#64748b",
          style: (e.data as any)?.style,
        })),
      };
    },
    [structure?.id]
  );

  const graphFromState = useCallback(
    () => buildGraphPayload(rfStateRef.current.nodes, rfStateRef.current.edges),
    [buildGraphPayload]
  );

  const recordHistory = useCallback(
    (mode: "immediate" | "debounced", nodes?: any[], edges?: any[]) => {
      const snapshot = buildGraphPayload(nodes ?? rfStateRef.current.nodes, edges ?? rfStateRef.current.edges);
      if (mode === "immediate") {
        cancelQueue();
        push(snapshot);
      } else {
        queue(snapshot);
      }
    },
    [buildGraphPayload, cancelQueue, push, queue]
  );

  // Ref to store ws for callbacks that are defined before ws
  const wsRef = useRef<any>(null);

  const toggleCollapse = useCallback(
    (id: string) => {
      if (viewOnlyMode) return;
      const { nodes: currentNodes, edges: currentEdges } = rfStateRef.current;
      const targetNode = currentNodes.find((n) => n.id === id);
      const newCollapsed = targetNode ? !(targetNode.data as NodeData).collapsed : false;

      const nodes = currentNodes.map((n) =>
        n.id === id ? { ...n, data: { ...(n.data as NodeData), collapsed: newCollapsed } } : n
      );
      applyGraphState(nodes, currentEdges);
      markDirty();
      recordHistory("immediate", nodes, currentEdges);

      // Broadcast node_collapsed via WebSocket
      if (wsRef.current?.isConnected) {
        console.log("[Editor] Sending node_collapsed for:", id, "collapsed:", newCollapsed);
        wsRef.current.sendOperation({
          type: "node_collapsed",
          node_id: id,
          collapsed: newCollapsed,
        });
      }
    },
    [applyGraphState, markDirty, recordHistory, viewOnlyMode]
  );

  useEffect(() => {
    toggleCollapseRef.current = toggleCollapse;
  }, [toggleCollapse]);


  const authToken = getAuthToken();
  const authTokenPayload = structureId ? getTokenPayloadForStructure(structureId) : null;
  const wsAuthToken = authTokenPayload?.can_edit ? authToken : null;
  const wsShareToken = structure?.share_token || shareToken || null;
  const ws = useCollaboration({
    structureId,
    structure,
    shareToken,
    viewOnlyMode,
    mapGraphState,
    resolveContacts,
    toggleCollapse,
    setRfNodes,
    setRfEdges,
    rfStateRef,
    recordHistory,
    markRemote,
    setDraftDirty,
    wsAuthToken,
    wsShareToken,
    wsRef,
  });

  const onConnect = useCallback(
    (connection: Connection) => {
      if (viewOnlyMode) return;
      const edgeId = uuid();
      console.debug("onConnect", { ...connection, id: edgeId });
      const sourceHandle = normalizeHandleId(connection.sourceHandle);
      const targetHandle = normalizeHandleId(connection.targetHandle);

      const newEdge = {
        ...connection,
        id: edgeId,
        type: "default",
        sourceHandle,
        targetHandle,
        data: { arrow_style: "default" as const, line_style: "bezier" as const, routing: { mode: "auto", bendOffset: 0 } },
        markerEnd: { type: "arrowclosed" as const },
      };

      const updatedEdges = addEdge(newEdge, rfStateRef.current.edges);
      const nodes = rfStateRef.current.nodes;
      applyGraphState(nodes, updatedEdges);
      markDirty();
      recordHistory("immediate", nodes, updatedEdges);

      // Send edge_added operation
      if (ws.isConnected) {
        ws.sendOperation({
          type: "edge_added",
          edge: {
            id: edgeId,
            source: connection.source!,
            target: connection.target!,
            source_handle: sourceHandle,
            target_handle: targetHandle,
            arrow_style: "default",
            line_style: (newEdge as any).data?.line_style || "bezier",
            routing: (newEdge as any).data?.routing || { mode: "auto", bendOffset: 0 },
          },
        });
      }
    },
    [applyEdgePatch, applyGraphState, markDirty, recordHistory, viewOnlyMode, ws]
  );

  useEffect(() => {
    loadGraph().then(() => {
      setDirty(false);
      setDirtyTick(0);
      if (!viewOnlyMode && autoOpenSettingsFromNav) {
        setShowSettings(true);
      }
      // Fit view after loading - not in dependencies to prevent reload loops
      setTimeout(() => fitView({ padding: 0.2 }), 100);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadGraph, autoOpenSettingsFromNav]);

  useEffect(() => {
    setRfNodes((prev) =>
      prev.map((n) => {
        const data = n.data as NodeData;
        const contactIds = (data as any).contact_ids || [];
        const contacts = resolveContacts(data, contactLookup);
        const { board: boardContactsCount, group: groupContactsCount } = getContactCounts(contacts as any);
        return {
          ...n,
          data: {
            ...data,
            contact_ids: contactIds,
            contacts,
            contactsCount: contacts.length || contactIds.length,
            boardContactsCount,
            groupContactsCount,
          },
        };
      })
    );

    setOverviewNode((prev) => {
      if (!prev) return prev;
      const contactIds = (prev as any).contact_ids || [];
      const contacts = resolveContacts(prev, contactLookup);
      const { board: boardContactsCount, group: groupContactsCount } = getContactCounts(contacts as any);
      return {
        ...prev,
        contact_ids: contactIds,
        contacts,
        contactsCount: contacts.length || contactIds.length,
        boardContactsCount,
        groupContactsCount,
      };
    });
  }, [contactLookup, resolveContacts, setOverviewNode, setRfNodes]);

  useEffect(() => {
    if (structure) {
      setSettingsName(structure.name || "");
      setSettingsDescription(structure.description || "");
    }
  }, [structure]);

  useEffect(() => {
    sidebarOpenRef.current = sidebarOpen;
  }, [sidebarOpen]);

  useEffect(() => {
    if (saveError || error) {
      setStatusDismissed(false);
    }
  }, [error, saveError]);

  useEffect(() => {
    if (viewOnlyMode && showPngControls) {
      setShowPngControls(false);
    }
  }, [viewOnlyMode, showPngControls]);

  // Mobile detection and responsive sidebar handling
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768; // md breakpoint
      setIsMobile(mobile);
      const wasMobile = wasMobileRef.current;
      // Auto-close only when switching to mobile, not on every resize (keyboard)
      if (mobile && !wasMobile && sidebarOpenRef.current) {
        setSidebarOpen(false);
      }
      wasMobileRef.current = mobile;
    };

    checkMobile();

    // Debounce resize events to prevent excessive re-renders
    let resizeTimer: number;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(checkMobile, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []); // Only run on mount and cleanup

  const persist = useCallback(async () => {
    if (!structure || viewOnlyMode) return;
    if (savingRef.current) {
      saveQueuedRef.current = true;
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = graphFromState();
      const res = await api.saveGraph(structure.id, payload);
      console.debug("saveGraph success", res);

      // Don't overwrite the current state after save!
      // The user might have made more changes while save was in progress.
      // Only mark as saved and update timestamp.
      setDirty(false);
      setLastSavedAt(new Date().toLocaleTimeString());
    } catch (err: any) {
      console.error("saveGraph error", err);
      setError(err.message);
      setSaveError(err.message);
      // Keep dirty flag on error so user can retry
    } finally {
      savingRef.current = false;
      setSaving(false);
      if (saveQueuedRef.current) {
        saveQueuedRef.current = false;
        // Retry latest snapshot
        persist();
      }
    }
  }, [structure, viewOnlyMode, graphFromState]);

  useAutosave({ structureId: structure?.id, viewOnly: viewOnlyMode, dirty, dirtyTick, persist, delay: AUTOSAVE_DELAY_MS });

  useEffect(() => {
    // When hideInactive changes, reapply visibility
    const { nodes, edges } = rfStateRef.current;
    applyGraphState(nodes, edges);
  }, [applyGraphState, hideInactive]);

  const matchesSearch = useCallback(
    (node: NodeData) => {
      if (!normalizedSearch) return false;
      const labelMatch = (node.label || "").toLowerCase().includes(normalizedSearch);
      const descMatch = (node.description || "").toLowerCase().includes(normalizedSearch);
      const contactMatch = contactsForSearch(node).some((c: any) => {
        const values = [c.name, c.role, c.email, c.phone, c.signal, c.matrix, c.telegram, c.website];
        return values.some((v) => v && v.toString().toLowerCase().includes(normalizedSearch));
      });
      return labelMatch || descMatch || contactMatch;
    },
    [contactLookup, contactsForSearch, normalizedSearch]
  );

  useEffect(() => {
    const { nodes, edges } = rfStateRef.current;
    const highlightedNodes = nodes.map((n) => {
      const match = searchTerm && matchesSearch(n.data as NodeData);
      return { ...n, data: { ...(n.data as NodeData), highlighted: !!match } };
    });
    applyGraphState(highlightedNodes, edges);
    if (searchTerm) {
      const match = highlightedNodes.find((n) =>
        matchesSearch(n.data as NodeData)
      );
      if (match) {
        fitView({ nodes: [{ id: match.id }], padding: 0.4 });
      }
    }
  }, [applyGraphState, contactLookup, fitView, matchesSearch, searchTerm]);

  const handleSidebarNodeSelect = useCallback(
    (id: string) => {
      setSelectedNodeIds([id]);
      setSelectedEdgeIds([]);
      fitView({ nodes: [{ id }], padding: 0.4 });
      if (isMobile) {
        setSidebarOpen(false);
      }
    },
    [fitView, isMobile, setSelectedEdgeIds, setSelectedNodeIds, setSidebarOpen]
  );

  const onNodesChange = useCallback(
    (changes) => {
      if (viewOnlyMode) return;
      const structuralChange = changes.some((c: any) => c.type !== "select");
      const isDragging = changes.some((c: any) => c.type === "position" && c.dragging);
      const dragEnded = changes.some((c: any) => c.type === "position" && c.dragging === false);
      const nextNodes = applyNodeChanges(changes, rfStateRef.current.nodes);
      const nextEdges = rfStateRef.current.edges;

      applyGraphState(nextNodes, nextEdges, { throttle: isDragging });

      if (!structuralChange) return;
      if (dragEnded || changes.some((c: any) => c.type === "add" || c.type === "remove")) {
        markDirty();
        recordHistory("immediate", nextNodes, nextEdges);

        // Send operations via WebSocket
        if (ws.isConnected) {
          changes.forEach((change: any) => {
            // Handle node moved
            if (change.type === "position" && change.dragging === false) {
              const node = nextNodes.find((n) => n.id === change.id);
              if (node && node.position) {
                console.log("[Editor] Sending node_moved for:", change.id);
                ws.sendOperation({
                  type: "node_moved",
                  node_id: change.id,
                  x: node.position.x,
                  y: node.position.y,
                });
              }
            }
            // Handle node removed
            else if (change.type === "remove") {
              console.log("[Editor] Sending node_deleted for:", change.id);
              ws.sendOperation({
                type: "node_deleted",
                node_id: change.id,
              });
            }
          });
        }
      } else if (!isDragging) {
        markDirty();
        recordHistory("debounced", nextNodes, nextEdges);
      }
    },
    [applyGraphState, markDirty, recordHistory, viewOnlyMode, ws]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      if (viewOnlyMode) return;
      const nextEdges = applyEdgeChanges(changes, rfStateRef.current.edges);
      const nextNodes = rfStateRef.current.nodes;
      const structuralChange = changes.some((c: any) => c.type !== "select");
      applyGraphState(nextNodes, nextEdges);
      if (!structuralChange) return;
      markDirty();
      recordHistory("immediate", nextNodes, nextEdges);

      // Send edge operations via WebSocket
      if (ws.isConnected) {
        changes.forEach((change: any) => {
          if (change.type === "remove") {
            console.log("[Editor] Sending edge_deleted for:", change.id);
            ws.sendOperation({
              type: "edge_deleted",
              edge_id: change.id,
            });
          }
        });
      }
    },
    [applyGraphState, markDirty, recordHistory, viewOnlyMode, ws]
  );

  const onSelectionChange = useCallback(
    ({ nodes, edges }: { nodes: Node[]; edges: Edge[] }) => {
      setSelectedNodeIds(nodes.map((n) => n.id));
      setSelectedEdgeIds(edges.map((e) => e.id));
      if (viewOnlyMode && nodes.length > 0) {
        const nodeId = nodes[0].id;
        const current = rfStateRef.current.nodes.find((node) => node.id === nodeId);
        if (current) setOverviewNode(current.data as NodeData);
      } else if (viewOnlyMode && nodes.length === 0) {
        setOverviewNode(null);
      }
    },
    [viewOnlyMode]
  );

  const addNode = () => {
    if (viewOnlyMode) return;
    const next = blankNode();
    const typeCfg = nodeTypeById(newNodeType);
    next.type = newNodeType;
    next.color = typeCfg?.defaultColor || next.color;
    next.text_color = typeCfg?.defaultTextColor || next.text_color;
    next.shape = typeCfg?.shape || "rectangle";
    next.position = { x: Math.random() * 400 + 80, y: Math.random() * 260 + 120 };
    const { edges } = rfStateRef.current;
    const nodes = [
      ...rfStateRef.current.nodes,
      {
        id: next.id,
        type: getReactFlowType(next.shape),
        data: { ...next, onToggleCollapse: () => toggleCollapse(next.id) },
        position: next.position,
        draggable: true,
        selectable: true,
      },
    ];
    applyGraphState(nodes, edges);
    markDirty();
    recordHistory("immediate", nodes, edges);

    // Broadcast node_added via WebSocket
    if (ws.isConnected) {
      console.log("[Editor] Sending node_added for:", next.id);
      ws.sendOperation({
        type: "node_added",
        node: {
          ...next,
          x: next.position.x,
          y: next.position.y,
        },
      });
    }
  };

  const updateSelectedNode = (patch: Partial<NodeData>) => {
    const targetId = primarySelectedNodeId;
    if (!targetId || viewOnlyMode) return;
    const { nodes: currentNodes, edges } = rfStateRef.current;
    const nodes = currentNodes.map((n) =>
      n.id === targetId ? { ...n, data: { ...(n.data as NodeData), ...patch } } : n
    );
    applyGraphState(nodes, edges);
    markDirty();
    recordHistory("debounced", nodes, edges);

    // Broadcast node_updated via WebSocket
    if (ws.isConnected) {
      console.log("[Editor] Sending node_updated for:", targetId, "patch:", patch);
      ws.sendOperation({
        type: "node_updated",
        node_id: targetId,
        patch: patch,
      });
    }
  };

  const updateDraftNode = (updater: (prev: NodeData) => NodeData) => {
    setDraftNode((prev) => {
      if (!prev) return prev;
      setDraftDirty(true);
      return updater(prev);
    });
  };

  const applyDraftNode = () => {
    if (!draftNode || !selectedNode || viewOnlyMode) return;
    const normalizedContacts = (draftNode.contacts || []).map((c) => {
      const normalizedType = normalizeContactType(c.contact_type || (c as any).contactType || (c as any).type);
      return {
        ...c,
        contact_type: normalizedType || (c as any).contact_type || (c as any).type,
      };
    });
    const payload: NodeData = { ...draftNode, contacts: normalizedContacts, contact_roles: draftNode.contact_roles || {} };
    if (import.meta.env.DEV) {
      const { board, group } = getContactCounts(normalizedContacts as any);
      console.debug("[applyDraftNode]", payload.id, "contacts", normalizedContacts.length, "counts", { board, group });
    }
    updateSelectedNode(payload);
    setDraftDirty(false);
  };

  const updateSelectedEdge = (patch: Partial<EdgeData>) => {
    const selectedEdgeId = primarySelectedEdgeId;
    if (!selectedEdgeId || viewOnlyMode) return;
    const { nodes, edges: currentEdges } = rfStateRef.current;
    const edges = currentEdges.map((e) => (e.id === selectedEdgeId ? applyEdgePatch(e, patch) : e));
    applyGraphState(nodes, edges);
    markDirty();
    recordHistory("debounced", nodes, edges);

    // Broadcast edge_updated via WebSocket
    if (ws.isConnected) {
      console.log("[Editor] Sending edge_updated for:", selectedEdgeId, "patch:", patch);
      ws.sendOperation({
        type: "edge_updated",
        edge_id: selectedEdgeId,
        patch: patch,
      });
    }
  };

  const handleUndo = useCallback(() => {
    const snapshot = undo();
    if (snapshot) {
      cancelQueue();
      applySnapshot(snapshot);
      markDirty();
      setDraftDirty(false);
    }
  }, [applySnapshot, cancelQueue, markDirty, undo]);

  const handleRedo = useCallback(() => {
    const snapshot = redo();
    if (snapshot) {
      cancelQueue();
      applySnapshot(snapshot);
      markDirty();
      setDraftDirty(false);
    }
  }, [applySnapshot, cancelQueue, markDirty, redo]);

  const settingsDirty = useMemo(() => {
    if (!structure) return false;
    return (
      settingsName.trim() !== (structure.name || "") ||
      settingsDescription !== (structure.description || "")
    );
  }, [settingsDescription, settingsName, structure]);

  const closeSettings = useCallback(() => {
    if (!settingsDirty) {
      setShowSettings(false);
      setSettingsMessage(null);
      return;
    }
    setConfirmDialog({
      title: "Änderungen verwerfen?",
      description: "Ungespeicherte Änderungen gehen verloren.",
      confirmLabel: "Verwerfen",
      cancelLabel: "Abbrechen",
      tone: "danger",
      onConfirm: () => {
        setShowSettings(false);
        setSettingsMessage(null);
      },
    });
  }, [settingsDirty, setConfirmDialog]);

  const saveSettings = useCallback(async () => {
    if (!structure) return;
    setSettingsSaving(true);
    setSettingsMessage(null);
    try {
      const updated = await api.updateStructure(structure.id, {
        name: settingsName.trim(),
        description: settingsDescription,
      });
      setStructure(updated);
      setShowSettings(false);
      setSettingsMessage("Gespeichert");
      console.info("Structure updated", updated);
    } catch (err: any) {
      console.error("updateStructure error", err);
      setSettingsMessage(err.message || "Speichern fehlgeschlagen");
    } finally {
      setSettingsSaving(false);
    }
  }, [settingsDescription, settingsName, structure]);

  const deleteStructure = useCallback(async () => {
    if (!structure) return;
    setSettingsDeleteLoading(true);
    try {
      await api.deleteStructure(structure.id);
      setShowSettings(false);
      navigate("/", { replace: true });
    } catch (err: any) {
      setError(err.message || "Löschen fehlgeschlagen");
    } finally {
      setSettingsDeleteLoading(false);
    }
  }, [navigate, structure]);

  const isTyping = useCallback(() => {
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName.toLowerCase();
    const formTags = ["input", "textarea", "select", "option", "button"];
    return el.isContentEditable || formTags.includes(tag);
  }, []);

  const performDeleteSelection = useCallback(() => {
    const { nodes: currentNodes, edges: currentEdges } = rfStateRef.current;
    const nodeIdSet = new Set(selectedNodeIds);
    const edgeIdSet = new Set(selectedEdgeIds);
    const nodes = currentNodes.filter((n) => !nodeIdSet.has(n.id));
    const removedEdgeIds: string[] = currentEdges
      .filter((e) => edgeIdSet.has(e.id) || nodeIdSet.has(e.source) || nodeIdSet.has(e.target))
      .map((e) => e.id);
    const edges = currentEdges.filter(
      (e) => !edgeIdSet.has(e.id) && !nodeIdSet.has(e.source) && !nodeIdSet.has(e.target)
    );
    applyGraphState(nodes, edges);
    setSelectedNodeIds([]);
    setSelectedEdgeIds([]);
    markDirty();
    recordHistory("immediate", nodes, edges);

    // Broadcast removals to collaborators
    if (ws.isConnected) {
      selectedNodeIds.forEach((id) => {
        ws.sendOperation({ type: "node_deleted", node_id: id });
      });
      selectedEdgeIds.forEach((id) => {
        ws.sendOperation({ type: "edge_deleted", edge_id: id });
      });
      removedEdgeIds.forEach((id) => {
        if (!selectedEdgeIds.includes(id)) {
          ws.sendOperation({ type: "edge_deleted", edge_id: id });
        }
      });
    }
  }, [applyGraphState, markDirty, recordHistory, selectedEdgeIds, selectedNodeIds, ws]);

  const deleteSelection = useCallback(() => {
    if (viewOnlyMode) return;
    const hasNodes = selectedNodeIds.length > 0;
    const hasEdges = selectedEdgeIds.length > 0;
    if (!hasNodes && !hasEdges) return;

    if (hasNodes) {
      setConfirmDialog({
        title: hasEdges ? "Nodes und Kanten löschen?" : "Nodes löschen?",
        description: "Zugehörige Verbindungen werden entfernt.",
        confirmLabel: "Löschen",
        cancelLabel: "Abbrechen",
        tone: "danger",
        onConfirm: performDeleteSelection,
      });
      return;
    }
    performDeleteSelection();
  }, [performDeleteSelection, selectedEdgeIds, selectedNodeIds, setConfirmDialog, viewOnlyMode]);

  const focusSearch = useCallback(() => {
    if (!sidebarOpen) {
      setSidebarOpen(true);
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      });
      return;
    }
    searchInputRef.current?.focus();
    searchInputRef.current?.select();
  }, [sidebarOpen]);

  const focusOrFit = useCallback(
    (fitAll?: boolean) => {
      const { nodes } = rfStateRef.current;
      if (!fitAll && primarySelectedNodeId) {
        fitView({ nodes: [{ id: primarySelectedNodeId }], padding: 0.4 });
        return;
      }
      if (nodes.length > 0) {
        fitView({ padding: 0.2 });
      }
    },
    [fitView, primarySelectedNodeId]
  );

  const updateSelectedEdgeStyle = useCallback(
    (patch: Partial<EdgeData>) => {
      if (viewOnlyMode || selectedEdgeIds.length === 0) return;
      const { nodes: currentNodes, edges: currentEdges } = rfStateRef.current;
      const edgeIdSet = new Set(selectedEdgeIds);
      const edges = currentEdges.map((e) => (edgeIdSet.has(e.id) ? applyEdgePatch(e, patch) : e));
      applyGraphState(currentNodes, edges);
      markDirty();
      recordHistory("immediate", currentNodes, edges);
    },
    [applyEdgePatch, applyGraphState, markDirty, recordHistory, selectedEdgeIds, viewOnlyMode]
  );

  const duplicateSelection = useCallback(() => {
    if (viewOnlyMode || selectedNodeIds.length === 0) return;
    const { nodes: currentNodes, edges: currentEdges } = rfStateRef.current;
    const nodeIdSet = new Set(selectedNodeIds);
    const newNodes: any[] = [];
    currentNodes.forEach((n) => {
      if (!nodeIdSet.has(n.id)) return;
      const pos = n.position || { x: 0, y: 0 };
      const clonedId = uuid();
      newNodes.push({
        ...n,
        id: clonedId,
        position: { x: pos.x + 30, y: pos.y + 30 },
        data: { ...(n.data as NodeData), id: clonedId, label: `${(n.data as NodeData).label} Kopie` },
        selected: false,
      });
    });
    if (newNodes.length === 0) return;
    const nodes = [...currentNodes, ...newNodes];
    applyGraphState(nodes, currentEdges);
    markDirty();
    recordHistory("immediate", nodes, currentEdges);

    if (ws.isConnected) {
      newNodes.forEach((n) => {
        const data = n.data as NodeData;
        const { onToggleCollapse, viewOnly, highlighted, ...serializable } = data as any;
        ws.sendOperation({
          type: "node_added",
          node: {
            ...serializable,
            x: n.position?.x ?? 0,
            y: n.position?.y ?? 0,
          },
        });
      });
    }
  }, [applyGraphState, markDirty, recordHistory, selectedNodeIds, viewOnlyMode, ws]);

  const applyBulkNodes = useCallback(
    (patch: Partial<NodeData>) => {
      if (viewOnlyMode || selectedNodeIds.length === 0) return;
      const { nodes: currentNodes, edges } = rfStateRef.current;
      const idSet = new Set(selectedNodeIds);
      const nodes = currentNodes.map((n) =>
        idSet.has(n.id) ? { ...n, data: { ...(n.data as NodeData), ...patch } } : n
      );
      applyGraphState(nodes, edges);
      markDirty();
      recordHistory("immediate", nodes, edges);

      if (ws.isConnected) {
        selectedNodeIds.forEach((id) => {
          ws.sendOperation({
            type: "node_updated",
            node_id: id,
            patch,
          });
        });
      }
    },
    [applyGraphState, markDirty, recordHistory, selectedNodeIds, viewOnlyMode, ws]
  );

  const applyBulkEdges = useCallback(
    (patch: Partial<EdgeData>) => {
      if (viewOnlyMode || selectedEdgeIds.length === 0) return;
      const { nodes, edges: currentEdges } = rfStateRef.current;
      const idSet = new Set(selectedEdgeIds);
      const edges = currentEdges.map((e) => (idSet.has(e.id) ? applyEdgePatch(e, patch) : e));
      applyGraphState(nodes, edges);
      markDirty();
      recordHistory("immediate", nodes, edges);

      if (ws.isConnected) {
        selectedEdgeIds.forEach((id) => {
          ws.sendOperation({
            type: "edge_updated",
            edge_id: id,
            patch,
          });
        });
      }
    },
    [applyEdgePatch, applyGraphState, markDirty, recordHistory, selectedEdgeIds, viewOnlyMode, ws]
  );

  useEditorHotkeys({
    isTyping,
    showSettings,
    closeSettings,
    setSelectedNodeIds,
    setSelectedEdgeIds,
    showHotkeys,
    setShowHotkeys,
    overviewNode,
    setOverviewNode,
    handleUndo,
    handleRedo,
    deleteSelection,
    focusSearch,
    focusOrFit,
    persist,
    duplicateSelection,
    addNode,
    selectedEdgeIds,
    updateSelectedEdgeStyle: (patch: Partial<EdgeData>) => {
      // Preserve toggle reverse behaviour
      if (patch.arrow_style === "reverse" && selectedEdge) {
        const currentStyle = ((selectedEdge.data as EdgeData | undefined)?.arrow_style as EdgeData["arrow_style"]) || "default";
        const next = currentStyle === "reverse" ? "default" : "reverse";
        updateSelectedEdgeStyle({ arrow_style: next });
        return;
      }
      updateSelectedEdgeStyle(patch);
    },
  });

  const handleExport = async () => {
    if (!structure) return;
    try {
      const payload = await api.exportBackup(structure.id);
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${structure.name || "structure"}-backup.json`;
      a.click();
      URL.revokeObjectURL(url);
      setSettingsMessage("Export abgeschlossen. Backup gespeichert.");
    } catch (e: any) {
      setSettingsMessage(e.message || "Export fehlgeschlagen");
    }
  };

  const importFile = async (evt: ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const res = await api.importBackup(payload, "create_new");
      if (res?.structure_id) {
        setSettingsMessage("Import abgeschlossen. Struktur geöffnet.");
        navigate(`/s/${res.structure_id}`);
      } else {
        await loadGraph();
        setSettingsMessage("Import abgeschlossen und aktueller Graph aktualisiert.");
      }
    } catch (err: any) {
      setSettingsMessage(err.message || "Import fehlgeschlagen");
    } finally {
      setImporting(false);
      evt.target.value = "";
    }
  };

  const exportPng = useCallback(async () => {
    if (!flowWrapperRef.current) return;
    const rfEl = flowWrapperRef.current.querySelector(".react-flow");
    if (!rfEl) {
      setSettingsMessage("Screenshot fehlgeschlagen: Canvas nicht gefunden.");
      return;
    }

    const clampNumber = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

    const originalTheme = theme;
    const targetTheme = pngThemeChoice === "current" ? originalTheme : pngThemeChoice;
    const switchedTheme = targetTheme !== originalTheme;

    setPngExporting(true);
    try {
      if (switchedTheme) {
        setTheme(targetTheme);
        await new Promise((resolve) => setTimeout(resolve, 80));
      }
      const canvasBg =
        pngBackgroundChoice === "canvas"
          ? getComputedStyle(document.documentElement).getPropertyValue("--color-canvas")?.trim() || undefined
          : undefined;

      const bounds = (rfEl as HTMLElement).getBoundingClientRect();
      const longestSide = Math.max(bounds.width, bounds.height, 1);
      // Aim for a larger exported resolution; clamp to avoid huge canvases.
      const targetLongSide = 3600;
      const pixelRatio = clampNumber(targetLongSide / longestSide, 2, 4);

      // skipFonts avoids html-to-image font normalization issues (undefined font family)
      const dataUrl = await toPng(rfEl as HTMLElement, {
        pixelRatio,
        skipFonts: true,
        backgroundColor: canvasBg,
      });
      const link = document.createElement("a");
      const safeName = (structure?.name || "atlas-graph").replace(/[^a-z0-9-_]+/gi, "_");
      link.download = `${safeName}.png`;
      link.href = dataUrl;
      link.click();
      setSettingsMessage("PNG-Export abgeschlossen.");
    } catch (err: any) {
      console.error("exportPng error", err);
      setSettingsMessage(err?.message || "PNG Export fehlgeschlagen");
    } finally {
      if (switchedTheme) setTheme(originalTheme);
      setPngExporting(false);
    }
  }, [flowWrapperRef, pngThemeChoice, setTheme, structure?.name, theme]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rfNodes.forEach((n) => {
      counts[(n.data as NodeData).type] = (counts[(n.data as NodeData).type] || 0) + 1;
    });
    return counts;
  }, [rfNodes]);
  const saveStateLabel = saving ? "Speichert..." : saveError ? "Speicherfehler" : dirty ? "Änderungen" : "Gespeichert";
  const saveToneClass = saving
    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
    : saveError
    ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200"
    : dirty
    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
    : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200";

  const inspectorTabs: { id: "general" | "contacts" | "links" | "notes"; label: string }[] = useMemo(() => {
    const tabs: { id: "general" | "contacts" | "links" | "notes"; label: string }[] = [
      { id: "general", label: "Allgemein" },
    ];

    if (structureTypeConfig.features?.contacts) {
      tabs.push({ id: "contacts", label: "Kontakte" });
    }
    if (structureTypeConfig.features?.links) {
      tabs.push({ id: "links", label: "Links" });
    }
    if (structureTypeConfig.features?.notes) {
      tabs.push({ id: "notes", label: "Notizen" });
    }

    return tabs;
  }, [structureTypeConfig]);

  const inspector = (
    <Inspector
      viewOnly={viewOnlyMode}
      isBulk={isBulk}
      selectedNodesCount={selectedNodes.length}
      selectedEdgesCount={selectedEdges.length}
      selectedEdge={selectedEdge}
      selectedNode={selectedNode}
      inspectorTab={inspectorTab}
      inspectorTabs={inspectorTabs}
      bulkNodeType={bulkNodeType}
      setBulkNodeType={setBulkNodeType}
      bulkNodeColor={bulkNodeColor}
      setBulkNodeColor={setBulkNodeColor}
      bulkTextColor={bulkTextColor}
      setBulkTextColor={setBulkTextColor}
      bulkEdgeColor={bulkEdgeColor}
      setBulkEdgeColor={setBulkEdgeColor}
      nodeTemplates={nodeTemplates}
      nodeTypeById={nodeTypeById}
      currentNodeData={currentNodeData}
      draftDirty={draftDirty}
      structureId={structureId}
      structureTypeConfig={structureTypeConfig}
      setInspectorTab={setInspectorTab}
      applyBulkNodes={applyBulkNodes}
      applyBulkEdges={applyBulkEdges}
      deleteSelection={deleteSelection}
      updateSelectedEdge={updateSelectedEdge}
      primarySelectedEdgeId={primarySelectedEdgeId}
      setConfirmDialog={setConfirmDialog}
      setRfEdges={setRfEdges}
      setSelectedEdgeIds={setSelectedEdgeIds}
      setDirty={setDirty}
      updateDraftNode={updateDraftNode}
      updateSelectedNode={updateSelectedNode}
      applyDraftNode={applyDraftNode}
      setDraftNode={setDraftNode}
      setDraftDirty={setDraftDirty}
    />
  );

  if (!structureId && !shareToken) {
    return <div className="p-10 text-red-500">Keine Struktur gewählt.</div>;
  }

  // Diagnostics panel removed - status is shown in topbar

  return (
    <div className="h-screen flex flex-col bg-[var(--color-bg)]">
      <TopBar
        structureName={structure?.name}
        structureDescription={structure?.description}
        saveToneClass={saveToneClass}
        saveStateLabel={saveStateLabel}
        lastSavedAt={lastSavedAt}
        wsConnected={ws.isConnected}
        wsPresence={ws.presenceCount}
        viewOnly={viewOnlyMode}
        canUndo={canUndo}
        canRedo={canRedo}
        undoHint={!canUndo && lockIndex !== null ? "Undo bis zur letzten Remote-Änderung gesperrt" : null}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onFitView={() => fitView()}
        onOpenContacts={() => setContactBookOpen(true)}
        onAddNode={addNode}
        newNodeType={newNodeType}
        nodeOptions={nodeTemplates}
        onNewNodeTypeChange={setNewNodeType}
        onSave={persist}
        saving={saving}
        onOpenSettings={() => setShowSettings(true)}
        onTogglePngControls={() => setShowPngControls((prev) => !prev)}
        pngExporting={pngExporting}
        onToggleTheme={toggle}
        theme={theme as "light" | "dark"}
      />
      {!viewOnlyMode && showPngControls && (
        <PngExportControls
          pngThemeChoice={pngThemeChoice}
          setPngThemeChoice={setPngThemeChoice}
          pngBackgroundChoice={pngBackgroundChoice}
          setPngBackgroundChoice={setPngBackgroundChoice}
          pngExporting={pngExporting}
          onExport={exportPng}
        />
      )}
      {showHotkeys && <HotkeysOverlay sections={hotkeySections} onClose={() => setShowHotkeys(false)} />}
      {(saveError || error) && !statusDismissed && (
        <InlineAlert
          tone="error"
          title={saveError ? "Speichern fehlgeschlagen" : "Fehler beim Laden"}
          message={saveError || error || "Unbekannter Fehler"}
          actionLabel={saveError ? "Erneut speichern" : "Nochmal laden"}
          onAction={saveError ? persist : loadGraph}
          dismissible
          onClose={() => setStatusDismissed(true)}
          className="mx-4 mb-2"
        />
      )}
      <div className="flex flex-1 overflow-hidden">
        {sidebarOpen && (
          <Sidebar
            nodes={rfNodes}
            typeCounts={typeCounts}
            getTypeLabel={(type) => nodeTypeById(type)?.label || type}
            searchTerm={searchTerm}
            normalizedSearch={normalizedSearch}
            onSearchChange={setSearchTerm}
            searchInputRef={searchInputRef}
            matchesSearch={matchesSearch}
            contactsForSearch={contactsForSearch}
            onSelectNode={handleSidebarNodeSelect}
            hideInactive={hideInactive}
            onToggleHideInactive={setHideInactive}
            onOpenContacts={() => setContactBookOpen(true)}
            onNavigateStructures={() => navigate("/")}
            viewOnlyMode={viewOnlyMode}
            onClose={() => setSidebarOpen(false)}
          />
        )}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="absolute left-2 top-20 z-10 p-2 bg-[var(--color-panel)] border border-[var(--color-border)] rounded-lg shadow-lg hover:bg-[var(--color-panel-2)] transition-colors"
            title="Sidebar öffnen"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        <Canvas
          nodes={rfNodes}
          edges={rfEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onSelectionChange={onSelectionChange}
          onNodeClick={(node) => {
            if (viewOnlyMode) {
              setSelectedNodeIds([node.id]);
              setSelectedEdgeIds([]);
              setOverviewNode(node.data as NodeData);
            }
          }}
          viewOnly={viewOnlyMode}
          theme={theme as "light" | "dark"}
          loading={loading}
          pngExporting={pngExporting}
          flowWrapperRef={flowWrapperRef}
        />
        {inspector}
      </div>
      {overviewNode && (
        <NodeOverview
          node={overviewNode}
          onClose={() => {
            setOverviewNode(null);
            setSelectedNodeIds([]);
            setSelectedEdgeIds([]);
          }}
          allNodes={rfNodes.map((n) => n.data as NodeData)}
          contactLookup={contactLookup}
        onNavigate={(nodeId) => {
            openOverviewForNode(nodeId);
          }}
        />
      )}
      {confirmDialog && (
        <ConfirmModal
          isOpen={true}
          title={confirmDialog.title}
          description={confirmDialog.description}
          confirmLabel={confirmDialog.confirmLabel}
          cancelLabel={confirmDialog.cancelLabel}
          tone={confirmDialog.tone}
          onConfirm={() => {
            const action = confirmDialog.onConfirm;
            setConfirmDialog(null);
            action();
          }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      {structure && (
        <ContactBookModal
          structureId={structure.id}
          isOpen={contactBookOpen}
          onClose={() => setContactBookOpen(false)}
          onNavigate={handleNavigateFromContactBook}
        />
      )}
      <SettingsModal
        isOpen={!!showSettings && !!structure}
        structure={structure}
        settingsTab={settingsTab}
        setSettingsTab={setSettingsTab}
        settingsName={settingsName}
        settingsDescription={settingsDescription}
        setSettingsName={setSettingsName}
        setSettingsDescription={setSettingsDescription}
        settingsMessage={settingsMessage}
        setSettingsMessage={setSettingsMessage}
        settingsSaving={settingsSaving}
        settingsDeleteLoading={settingsDeleteLoading}
        importing={importing}
        onClose={closeSettings}
        onSave={saveSettings}
        onDelete={() =>
          setConfirmDialog({
            title: "Struktur wirklich löschen?",
            description: "Dieser Vorgang kann nicht rückgängig gemacht werden.",
            confirmLabel: "Löschen",
            cancelLabel: "Abbrechen",
            tone: "danger",
            onConfirm: deleteStructure,
          })
        }
        onExport={handleExport}
        onImportFile={importFile}
        refetchNodeTypes={refetchNodeTypes}
        structureId={structureId}
        navigateToDebug={() => navigate("/debug/graph")}
      />
    </div>
  );
};

export const EditorPage: React.FC = () => {
  const { id } = useParams();
  if (!id) return <div className="p-6 text-red-500">Struktur nicht gefunden.</div>;
  return (
    <ReactFlowProvider>
      <EditorInner structureId={id} />
    </ReactFlowProvider>
  );
};

export const SharePage: React.FC = () => {
  const { token } = useParams();
  if (!token) return <div className="p-6 text-red-500">Share-Token fehlt.</div>;
  return (
    <ReactFlowProvider>
      <EditorInner shareToken={token} viewOnly />
    </ReactFlowProvider>
  );
};

export const DebugGraphPage: React.FC = () => {
  const nodes = [
    { id: "a", position: { x: 0, y: 0 }, data: { ...blankNode(), id: "a", label: "Alpha" }, type: "atlas" },
    { id: "b", position: { x: 300, y: 140 }, data: { ...blankNode(), id: "b", label: "Beta" }, type: "atlas" },
  ];
  const edges = [{ id: "edge-a-b", source: "a", target: "b", markerEnd: { type: "arrowclosed" } }];

  return (
    <div className="h-screen bg-slate-50 dark:bg-slate-950">
      <ReactFlowProvider>
        <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView>
          <Background gap={24} size={1} variant={BackgroundVariant.Dots} />
          <Controls />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};
